// Region-specific SEO config for Tradelog — powers /in, /us, /uk, /eu, /sea
// landing routes. Each region overrides title/description/canonical + a
// currency default so Google's hreflang alternates land the right locale
// on the right SERP, and users see local pricing on first paint.
//
// Also emits richer JSON-LD (FAQPage, HowTo, Product) so generative engines
// (ChatGPT, Perplexity, Gemini, Google AI Overviews) can quote structured
// answers instead of paraphrasing scraped HTML.

const SITE = "https://thetradlog.com";
const OG   = SITE + "/img/og-cover.png";

// ---- Region catalogue -----------------------------------------------------

export const REGIONS = {
  in: {
    code: "IN", hreflang: "en-IN", ccy: "INR", currencySymbol: "₹",
    country: "India",
    title: "Tradelog · Trading Journal + Prop Firm Simulator for Indian Traders (MCX, Zerodha, Upstox)",
    description: "India's most complete trading journal for prop-firm traders. Import Zerodha, Upstox, Groww, Angel One, Fyers, and Exness CSVs, trade MCX Gold/Silver/Crude on live charts, and pass a funded challenge under real prop-firm rules. Prices in ₹. 14-day free trial.",
    heroKicker: "MCX + Indian broker CSVs",
  },
  us: {
    code: "US", hreflang: "en-US", ccy: "USD", currencySymbol: "$",
    country: "United States",
    title: "Tradelog · Trading Journal + Prop Firm Simulator for US Day Traders (Topstep, Apex)",
    description: "US-first trading journal for prop-firm and futures day traders. Track NQ, ES, YM, GC, and CL fills, journal Topstep and Apex challenges, and pass a funded evaluation with a live rules engine. USD pricing. 14-day free trial.",
    heroKicker: "Topstep / Apex-ready",
  },
  uk: {
    code: "GB", hreflang: "en-GB", ccy: "GBP", currencySymbol: "£",
    country: "United Kingdom",
    title: "Tradelog · Trading Journal + Prop Firm Challenge Simulator (UK Edition)",
    description: "The trading journal UK prop-firm traders use to pass FTMO, FundedNext, and The5%ers challenges. MT4/MT5 statement import, live rules engine, discipline score. GBP pricing. 14-day free trial.",
    heroKicker: "FTMO / FundedNext ready",
  },
  eu: {
    code: "EU", hreflang: "en", ccy: "EUR", currencySymbol: "€",
    country: "European Union",
    title: "Tradelog · Trading Journal + Prop Firm Simulator (EU Edition)",
    description: "EU traders' choice for prop-firm journaling — MT4/MT5 import, real-rules challenge simulator, GDPR-safe encrypted broker sync (Binance, CoinDCX). Prices in €. 14-day free trial.",
    heroKicker: "GDPR-safe · MT4/MT5",
  },
  sea: {
    code: "SG", hreflang: "en-SG", ccy: "SGD", currencySymbol: "S$",
    country: "Singapore / SEA",
    title: "Tradelog · Trading Journal + Prop Firm Simulator (Singapore & SEA)",
    description: "Southeast Asia's trading journal for FTMO and MFF traders. Live charts on Bybit/Binance pairs, MT4/MT5 statement import, discipline score, funded-challenge simulator. Prices in S$. 14-day free trial.",
    heroKicker: "Bybit / Binance native",
  },
  global: {
    code: "GLOBAL", hreflang: "x-default", ccy: null, currencySymbol: null,
    country: "Global",
    title: "Tradelog by Yash Khajania · Trading Journal + Prop Firm Challenge Simulator",
    description: "Tradelog by Yash Khajania — the professional trading journal for prop-firm traders. Import broker CSVs (MT4/MT5/Zerodha/Exness/Binance), track daily P&L, discipline score and equity curve, and pass a real prop-firm challenge under strict funded rules. 14-day free trial.",
    heroKicker: null,
  },
};

// URL path each region lives at. "global" = "/".
export const REGION_PATH = {
  in: "/in", us: "/us", uk: "/uk", eu: "/eu", sea: "/sea", global: "/",
};

// ---- Hreflang alternates for a given canonical route ---------------------
// Used by useSeo → we render <link rel="alternate" hreflang="…" href="…" />
// so Google routes each locale's users to their region-specific page.

export function hreflangAlternatesFor(route = "/") {
  // `route` is the shared base path (e.g. "/", "/prop-challenge"). For the
  // homepage we alternate across /in, /us, /uk, /eu, /sea, /. For nested
  // routes we keep the same path since regional wrappers only exist for /.
  if (route === "/") {
    return Object.entries(REGIONS).map(([key, r]) => ({
      hreflang: r.hreflang,
      href: SITE + REGION_PATH[key],
    }));
  }
  // Nested routes: single x-default alternate.
  return [{ hreflang: "x-default", href: SITE + route }];
}

// ---- Rich JSON-LD payloads for AI-answer engines -------------------------

export function faqJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is Tradelog?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Tradelog is a professional trading journal and prop-firm challenge simulator built by Yash Khajania. It lets you import broker CSVs (MT4, MT5, Zerodha, Upstox, Exness, Binance), auto-sync trades from Binance/CoinDCX/Delta with encrypted API keys, track daily P&L on a calendar view, and pass a real funded-trader challenge under strict rules (drawdown, daily loss, min days, consistency, news lock).",
        },
      },
      {
        "@type": "Question",
        name: "Which prop firms does Tradelog simulate?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Tradelog simulates 5 tiers of MFTC-style challenges out of the box, and its rules engine also mirrors FTMO, FundedNext, MyForexFunds, The5%ers, and Topstep-style evaluations — 60% profit target, 10% daily loss, 30% max drawdown, minimum 6 trading days, 6-day deadline, max 4 trades/day, high-impact news trading blocked, overnight holds allowed.",
        },
      },
      {
        "@type": "Question",
        name: "How does Tradelog import trades from MT4 or MT5?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Drop your MT4 or MT5 HTML or CSV account statement into the /settings/brokers upload panel. Tradelog auto-detects the broker (XM, Vantage, IC Markets, Pepperstone, Exness, FBS, HF Markets, OctaFX), parses every closed ticket including entry/exit price, SL, TP, commission, swap and profit, and adds them to your journal with idempotent upsert so re-uploading the same statement never duplicates.",
        },
      },
      {
        "@type": "Question",
        name: "Does Tradelog auto-sync with my exchange?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Tradelog auto-syncs read-only API keys for Binance, CoinDCX, and Delta Exchange India, encrypted at rest with Fernet. A nightly cron at 3 AM UTC pulls new closed fills and inserts them into your journal automatically — no CSV upload needed.",
        },
      },
      {
        "@type": "Question",
        name: "How much does Tradelog cost?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Tradelog offers a 14-day free trial, no card required. Paid plans: Monthly ₹600 (~$7), Quarterly ₹1,500 (save ₹300), Yearly ₹4,500 (save ₹2,700). All plans include unlimited trades, MT4/MT5 statement import, and full analytics. Auto broker sync starts on the Quarterly plan.",
        },
      },
      {
        "@type": "Question",
        name: "Is Tradelog free?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Tradelog offers a 14-day free trial of the full product with no credit card required. Live charts remain free forever. The prop-firm simulator, discipline score, and full analytics are on the paid plan starting at ₹600 / month (about $7).",
        },
      },
      {
        "@type": "Question",
        name: "Who built Tradelog?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Tradelog is built by Yash Khajania, a full-stack developer and trader. The product is often referred to as 'Tradelog by Yash Khajania'.",
        },
      },
    ],
  };
}

export function howToPassChallengeJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to pass a prop-firm challenge with Tradelog",
    description: "Use Tradelog's paper prop-firm simulator to pass a funded evaluation on the first attempt.",
    totalTime: "P6D",
    supply: [{ "@type": "HowToSupply", name: "Tradelog account (free 14-day trial)" }],
    step: [
      { "@type": "HowToStep", name: "Pick your firm and tier",  text: "Choose MFTC, FTMO, FundedNext, or a custom tier. Tradelog loads the exact rules — 60% profit target, 10% daily loss, 30% max drawdown, 6 min trading days." },
      { "@type": "HowToStep", name: "Accept the rules",         text: "Read and agree to the full rule list on the trader-name form. Tradelog enforces every rule live during trading." },
      { "@type": "HowToStep", name: "Trade the paper challenge", text: "Place market or limit orders on Exness-style live charts. Tradelog tracks unrealized P&L, drawdown, daily loss, and news-lock windows automatically." },
      { "@type": "HowToStep", name: "Watch your discipline score", text: "Tradelog's rules engine flags over-trading, revenge trades, and news trades in real time so you spot rule-breaks before they end your challenge." },
      { "@type": "HowToStep", name: "Download your certificate", text: "On pass, Tradelog generates a scannable PDF certificate ('Midnight Emerald' design) with QR verification you can share on LinkedIn or in your prop-firm application." },
    ],
  };
}

export function productJsonLd(region = "global") {
  const r = REGIONS[region] || REGIONS.global;
  const ccy = r.ccy || "INR";
  const sym = r.currencySymbol || "₹";
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Tradelog · Trading Journal + Prop Firm Simulator",
    image: OG,
    description: r.description,
    brand: { "@type": "Brand", name: "Tradelog", founder: { "@type": "Person", name: "Yash Khajania" } },
    offers: [
      { "@type": "Offer", name: "Monthly",   price: "600",  priceCurrency: "INR", url: SITE + "/#pricing", availability: "https://schema.org/InStock" },
      { "@type": "Offer", name: "Quarterly", price: "1500", priceCurrency: "INR", url: SITE + "/#pricing", availability: "https://schema.org/InStock" },
      { "@type": "Offer", name: "Yearly",    price: "4500", priceCurrency: "INR", url: SITE + "/#pricing", availability: "https://schema.org/InStock" },
    ],
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.8", reviewCount: "127" },
  };
}

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Tradelog",
    alternateName: "Tradelog by Yash Khajania",
    founder: { "@type": "Person", name: "Yash Khajania" },
    url: SITE,
    logo: OG,
    sameAs: [],
    contactPoint: [{
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "hello@thetradlog.com",
      availableLanguage: ["English", "Hindi"],
      areaServed: ["IN", "US", "GB", "EU", "SG", "AU", "AE"],
    }],
  };
}

// ---- SEO builder used by <Landing /> when rendered under a region route --

export function getRegionSeo(region = "global") {
  const r = REGIONS[region] || REGIONS.global;
  const path = REGION_PATH[region] || "/";
  return {
    title: r.title,
    description: r.description,
    canonical: path,
    jsonLd: [
      organizationJsonLd(),
      productJsonLd(region),
      faqJsonLd(),
      howToPassChallengeJsonLd(),
    ],
  };
}

// Sync currency picker with regional default on first mount so a US visitor
// landing on /us instantly sees $ pricing (they can still change it manually).
export function applyRegionCurrency(region) {
  if (typeof window === "undefined") return;
  const r = REGIONS[region];
  if (!r || !r.ccy) return;
  try {
    // useCurrency reads `tj.ccy` from localStorage; only override if the
    // user hasn't already picked one.
    if (!localStorage.getItem("tj.ccy.userSet")) {
      localStorage.setItem("tj.ccy", r.ccy);
    }
  } catch {}
}
